import { useCallback, useEffect, useMemo, useState } from 'react'
import * as UUID from 'uuid'

function load<EntityT, JsonT = EntityT>(props: LocalStorageState<EntityT, JsonT>, hostPrefix: string): EntityT {
    if (props.key) {
        if (props.logging) {
            console.debug('LocalStorage', `loading ${hostPrefix}-${props.key} from localstorage`)
        }
        const rawValue = localStorage.getItem(`${hostPrefix}-${props.key}`)
        let jsonValues: JsonT = [null, undefined, 'null', 'undefined'].includes(rawValue) ? undefined : JSON.parse(rawValue)
        if (jsonValues === undefined) {
            if (props.logging) {
                console.debug('LocalStorage', `${hostPrefix}-${props.key} is empty in localstorage, falling back to defaultValue`)
            }
            jsonValues = props.defaultValue as any
        }

        return jsonValues !== undefined ? props.fromJson(jsonValues) : ({} as any)
    }
    return props.defaultValue
}

export type LocalStorageState<EntityT, JsonT = EntityT> = {
    key?: string // When key is undefined, this hook acts as a useState hook.
    hostKey?: string
    defaultValue?: EntityT
    fromJson: (json: JsonT) => EntityT
    toJson: (values: EntityT) => JsonT
    load?: boolean // Set this to false if you want to block the initial load.
    logging?: boolean
}

export type LocalStorageResult<EntityT> = {
    loaded: boolean
    values: EntityT
    setValues: (newValues: EntityT) => void
}

/**
 * This hook will load & persist the values to the LocalStorage using the given key. When a default value is provided it
 * will return this when LocalStorage is empty. When no key is provided, this hook acts as a useState hook.
 */
export default function useLocalStorage<EntityT, JsonT = EntityT>(props: LocalStorageState<EntityT, JsonT>): LocalStorageResult<EntityT> {
    const [hookId] = useState<string>(UUID.v4())
    const hostPrefix = useMemo<string>(() => (props.hostKey ? `${props.hostKey}` : 'global'), [props.hostKey])
    const storageKey = useMemo<string | undefined>(() => (props.key ? `${hostPrefix}-${props.key}` : undefined), [hostPrefix, props.key])
    const [values, setValues] = useState<EntityT>()
    const [loaded, setLoaded] = useState<boolean>(false)

    useEffect(() => {
        if (props.load !== false) {
            const initValues = load(props, hostPrefix)
            setValues(initValues)
            setLoaded(true)
        } else if (props.logging) {
            console.debug('LocalStorage', `'${hostPrefix}-${props.key}' is not yet ready to be loaded.`)
        }
    }, [props.hostKey, props.key, props.load])

    /**
     * Only load the values when the key matches, and we are not the hook that just set the values.
     */
    const eventHandler = useCallback(
        (evt: StorageEvent) => {
            if (evt.key === storageKey && evt.url !== hookId) {
                const newValues = load(props, hostPrefix)
                setValues(newValues)
                if (props.logging) {
                    console.debug('LocalStorage', `'${storageKey}' values changed, updating...`)
                }
            }
        },
        [storageKey, hookId, props.key, props.defaultValue, props.fromJson, hostPrefix],
    )

    useEffect(() => {
        if (props.key) {
            window.addEventListener('storage', eventHandler)
            return () => window.removeEventListener('storage', eventHandler)
        }
        return () => {}
    }, [props.key, props.load])

    /**
     * custom setter function that minimizes updates and dispatches a localstorage event.
     */
    const setterHandler = useCallback(
        (newValues: EntityT) => {
            const oldValuesString = JSON.stringify(props.toJson(values))
            const newValuesString = JSON.stringify(props.toJson(newValues))
            if (loaded && oldValuesString !== newValuesString) {
                if (props.logging) {
                    console.debug('LocalStorage', `values ${storageKey} changed, storing...`)
                }
                setValues(newValues)
                if (props.key) {
                    localStorage.setItem(`${storageKey}`, newValuesString)
                    window.dispatchEvent(
                        new StorageEvent('storage', {
                            key: storageKey,
                            oldValue: oldValuesString,
                            newValue: newValuesString,
                            url: hookId,
                        }),
                    )
                }
            } else if (loaded && props.logging) {
                console.debug('LocalStorage', `'${storageKey}' was updated, but values didn't change.`)
            } else if (props.logging) {
                console.debug('LocalStorage', `'${storageKey}' is not yet loaded.`)
            }
        },
        [values, loaded, storageKey, hookId, props.key, props.defaultValue, props.fromJson, hostPrefix],
    )

    return {
        loaded,
        values,
        setValues: setterHandler,
    }
}
